import React, {useEffect, useState} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {Chip, CircularProgress, makeStyles, TextField} from '@material-ui/core';
import axios from '../utils/axios';

const useStyles = makeStyles({
    autocomplete: {
        position: 'relative',
        flexGrow: 1
    },
    errorText: {
        color: '#f44336',
        position: 'absolute',
        bottom: 'calc(-0.75rem - 6px)',
        left: '0px',
        fontSize: '0.75rem',
    },
});

const PublisherMultivalueAutocompleteSearch = ({
                                          label,
                                          baseUrl,
                                          searchBy,
                                          searchQuery,
                                          getOptionLabel,
                                          identityAttribute = 'id',
                                          inputClassName = '',
                                          errorMessage = '',
                                          disabled = false,
                                          value = [],
                                          onValueChange,
                                          onError,
                                          error,
                                      }) => {
    const classes = useStyles();
    const [searchDelayTimer, setSearchDelayTimer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');


    const entityCache = React.useRef(new Map());

    useEffect(() => {
        if (!value?.length) {
            setSelectedOptions([]);
            return;
        }

        let cancelled = false;

        const fetchByIds = async () => {
            try {
                setLoading(true);

                const missingIds = value.filter(
                    (id) => !entityCache.current.has(id)
                );

                if (missingIds.length) {
                    const res = await axios.get(`${baseUrl}/by-ids`, {
                        params: { ids: missingIds },
                    });

                    for(var e of res.data?.data) {
                        entityCache.current.set(e[identityAttribute], e)
                    }
                }

                if (!cancelled) {
                    setSelectedOptions(
                        value
                            .map((id) => entityCache.current.get(id))
                            .filter(Boolean)
                    );
                }
            } catch (e) {
                if(onError)
                    onError(e);
            } finally {
                setLoading(false);
            }
        };

        fetchByIds();

        return () => {
            cancelled = true;
        };
    }, [value]);

    const handleInputChange = (_, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === 'input') {
            clearTimeout(searchDelayTimer);
            setSearchDelayTimer(
                setTimeout(async () => {
                    try {
                        setLoading(true);
                        const query = {
                            limit: 50,
                            ...(newInputValue && {[searchBy]: newInputValue}),
                            ...searchQuery,
                        };
                        const result = await axios(baseUrl, {params: query});
                        setOptions(result.data.data || []);
                    } catch (e) {
                        onError(e);
                    } finally {
                        setLoading(false);
                    }
                }, 400)
            );
        }
    };

    const handleChange = (_, newOptions) => {
        setSelectedOptions(newOptions);
        onValueChange(newOptions.map((o) => o[identityAttribute]));
    };

    return (
        <Autocomplete
            multiple
            className={`${classes.autocomplete} ${error ? 'Mui-error' : ''}`}
            options={options}
            value={selectedOptions}
            loading={loading}
            disabled={disabled}
            filterOptions={(x) => x}
            getOptionLabel={getOptionLabel}
            getOptionSelected={(opt, val) =>
                opt[identityAttribute] === val[identityAttribute]
            }
            getOptionDisabled={(option) =>
                value.some(
                    (v) => v[identityAttribute] === option[identityAttribute]
                )
            }
            onInputChange={handleInputChange}
            onChange={handleChange}
            noOptionsText="Nenalezeno"
            loadingText="Vyhledávání..."
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                    <Chip
                        label={getOptionLabel(option)}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        className={inputClassName}
                        label={label}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={18} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                    {error && errorMessage && (
                        <span className={classes.errorText}>{errorMessage}</span>
                    )}
                </>
            )}
        />
    );
};

export default PublisherMultivalueAutocompleteSearch;
